import { moduleFromContainerName } from './containerMapping';
import { MODULE_ROUTE, PATHNAME_MODULE_MAP } from './constant';
import type { SentryModule } from './types';

export { MODULE_ROUTE, PATHNAME_MODULE_MAP } from './constant';

/** One FormController shell (`TRANSACTIONS`) hosts payments / orders / refunds / disputes / suspicious. */
const PATHNAME_OWNED_CONTAINERS = new Set(['TRANSACTIONS']);

/** URL always wins — these must not inherit payments/home ownership. */
const PATH_LOCKED_MODULES = new Set<SentryModule>([
  'login',
  'signup',
  'suspicious',
  'orders',
  'refunds',
  'disputes',
]);

export const isPathLockedModule = (module?: SentryModule): boolean =>
  !!module && PATH_LOCKED_MODULES.has(module);

export const defaultRouteHints = (
  module: SentryModule,
  override?: readonly string[],
): readonly string[] => override ?? [MODULE_ROUTE[module]];

export const routeMatches = (
  routeOrPathname: string,
  routeHints?: readonly string[],
): boolean => {
  if (!routeHints?.length) return true;
  const haystack = routeOrPathname.toLowerCase();
  return routeHints.some(hint => haystack.includes(hint.toLowerCase()));
};

export const moduleFromPathname = (
  pathname?: string | null,
): SentryModule | undefined => {
  if (!pathname) return undefined;
  return PATHNAME_MODULE_MAP.find(({ prefix }) => pathname.includes(prefix))
    ?.module;
};

/** Prefer URL for shared shells so /transactions/disputes is not tagged payments.
 *  Login / signup / suspicious URLs always win so they are never owned by home/payments. */
export const resolveBoundaryModule = (
  moduleProp?: SentryModule,
  componentName?: string,
  pathname?: string | null,
): SentryModule | undefined => {
  const pathModule = moduleFromPathname(pathname);
  if (isPathLockedModule(pathModule)) {
    return pathModule;
  }
  if (componentName && PATHNAME_OWNED_CONTAINERS.has(componentName)) {
    return (
      pathModule ?? moduleProp ?? moduleFromContainerName(componentName)
    );
  }
  if (moduleProp) return moduleProp;
  return moduleFromContainerName(componentName) ?? pathModule;
};
